import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useTemplate,
    useCreateTemplate,
    useUpdateTemplate,
} from '../hooks';
import type {
    TemplateCategory,
    AssigneeType,
    CreateTemplateStepPayload,
} from '../types';
import { StepEditor } from '../components/StepEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const CATEGORIES: TemplateCategory[] = [
    'onboarding',
    'offboarding',
    'client_setup',
    'audit_prep',
    'office_move',
    'custom',
];

interface DraftStep {
    _key: string; // client-side drag key
    title: string;
    description: string;
    assignee_type: AssigneeType;
    specific_user_id: string;
    due_offset_days: number;
    evidence_required: boolean;
    depends_on_step_order: number | null; // 1-based order ref
}

function newDraftStep(): DraftStep {
    return {
        _key: crypto.randomUUID(),
        title: '',
        description: '',
        assignee_type: 'employee',
        specific_user_id: '',
        due_offset_days: 0,
        evidence_required: false,
        depends_on_step_order: null,
    };
}

export function TemplateBuilderPage() {
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    // ── Load existing template when editing ──
    const { data: existing, isLoading } = useTemplate(id ?? '');

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<TemplateCategory>('custom');
    const [steps, setSteps] = useState<DraftStep[]>([newDraftStep()]);
    const [hydrated, setHydrated] = useState(false);

    // Hydrate form once when editing
    if (isEdit && existing && !hydrated) {
        setName(existing.name);
        setDescription(existing.description);
        setCategory(existing.category);
        setSteps(
            (existing.steps ?? []).map((s) => ({
                _key: s.id,
                title: s.title,
                description: s.description,
                assignee_type: s.assignee_type,
                specific_user_id: s.specific_user_id ?? '',
                due_offset_days: s.due_offset_days,
                evidence_required: s.evidence_required,
                depends_on_step_order: s.depends_on_step_id
                    ? (existing.steps ?? []).findIndex(
                        (d) => d.id === s.depends_on_step_id
                    ) + 1 || null
                    : null,
            }))
        );
        setHydrated(true);
    }

    const createMut = useCreateTemplate();
    const updateMut = useUpdateTemplate();

    // ── Drag-and-drop reorder ──
    const [dragIdx, setDragIdx] = useState<number | null>(null);

    const handleDragStart = (idx: number) => setDragIdx(idx);
    const handleDragOver = (e: React.DragEvent, idx: number) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        setSteps((prev) => {
            const copy = [...prev];
            const [moved] = copy.splice(dragIdx, 1);
            copy.splice(idx, 0, moved);
            return copy;
        });
        setDragIdx(idx);
    };
    const handleDragEnd = () => setDragIdx(null);

    // ── Step CRUD ──
    const updateStep = useCallback(
        (key: string, patch: Partial<DraftStep>) =>
            setSteps((prev) =>
                prev.map((s) => (s._key === key ? { ...s, ...patch } : s))
            ),
        []
    );
    const removeStep = (key: string) =>
        setSteps((prev) => prev.filter((s) => s._key !== key));
    const addStep = () => setSteps((prev) => [...prev, newDraftStep()]);

    // ── Validation ──
    const validate = (): string | null => {
        if (!name.trim()) return 'Template name is required';
        if (steps.length === 0) return 'At least one step is required';
        for (let i = 0; i < steps.length; i++) {
            if (!steps[i].title.trim()) return `Step ${i + 1} needs a title`;
            if (steps[i].due_offset_days < 0)
                return `Step ${i + 1}: due offset cannot be negative`;
            const dep = steps[i].depends_on_step_order;
            if (dep !== null) {
                if (dep < 1 || dep > steps.length)
                    return `Step ${i + 1}: dependency order out of range`;
                if (dep === i + 1)
                    return `Step ${i + 1}: a step cannot depend on itself`;
            }
            if (
                steps[i].assignee_type === 'specific_user' &&
                !steps[i].specific_user_id.trim()
            )
                return `Step ${i + 1}: specific user ID required`;
        }
        // Cycle detection (simple DFS)
        const adj = new Map<number, number>();
        steps.forEach((s, i) => {
            if (s.depends_on_step_order !== null)
                adj.set(i + 1, s.depends_on_step_order);
        });
        for (const start of adj.keys()) {
            const visited = new Set<number>();
            let cur: number | undefined = start;
            while (cur !== undefined) {
                if (visited.has(cur))
                    return `Circular dependency detected involving step ${cur}`;
                visited.add(cur);
                cur = adj.get(cur);
            }
        }
        return null;
    };

    // ── Submit ──
    const handleSubmit = () => {
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }

        const stepsPayload: CreateTemplateStepPayload[] = steps.map((s) => ({
            title: s.title,
            description: s.description,
            assignee_type: s.assignee_type,
            specific_user_id:
                s.assignee_type === 'specific_user' ? s.specific_user_id : undefined,
            due_offset_days: s.due_offset_days,
            evidence_required: s.evidence_required,
            depends_on_step_order: s.depends_on_step_order ?? undefined,
        }));

        if (isEdit && id) {
            updateMut.mutate(
                { id, payload: { name, description, category, steps: stepsPayload } },
                { onSuccess: (t) => navigate(`/checklists/templates/${t.id}/edit`) }
            );
        } else {
            createMut.mutate(
                { name, description, category, steps: stepsPayload },
                { onSuccess: (t) => navigate(`/checklists/templates/${t.id}/edit`) }
            );
        }
    };

    if (isEdit && isLoading) {
        return <p className="text-muted-foreground">Loading template…</p>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">
                        {isEdit ? 'Edit Template' : 'New Template'}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {isEdit
                            ? `Editing v${existing?.version ?? 1} — saving creates a new version`
                            : 'Define reusable process steps'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/checklists/templates')}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={createMut.isPending || updateMut.isPending}
                    >
                        {createMut.isPending || updateMut.isPending
                            ? 'Saving…'
                            : isEdit
                                ? 'Save New Version'
                                : 'Create Template'}
                    </Button>
                </div>
            </div>

            {/* Meta */}
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium">Name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. New Starter Onboarding"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium">Category</label>
                    <Select
                        value={category}
                        onValueChange={(v) => setCategory(v as TemplateCategory)}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {CATEGORIES.map((c) => (
                                <SelectItem key={c} value={c}>
                                    {c.replace(/_/g, ' ')}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-3">
                    <label className="mb-1 block text-sm font-medium">Description</label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description"
                    />
                </div>
            </div>

            {/* Steps */}
            <div>
                <h2 className="mb-3 text-lg font-semibold">Steps</h2>
                <div className="space-y-3">
                    {steps.map((step, idx) => (
                        <div
                            key={step._key}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDragEnd={handleDragEnd}
                            className={`rounded-lg border p-4 transition ${dragIdx === idx ? 'border-primary bg-primary/5' : ''
                                }`}
                        >
                            <div className="mb-2 flex items-center justify-between">
                                <span className="cursor-grab text-sm font-medium text-muted-foreground">
                                    ☰ Step {idx + 1}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    onClick={() => removeStep(step._key)}
                                    disabled={steps.length === 1}
                                >
                                    Remove
                                </Button>
                            </div>
                            <StepEditor
                                step={step}
                                index={idx}
                                totalSteps={steps.length}
                                onChange={(patch) => updateStep(step._key, patch)}
                            />
                        </div>
                    ))}
                </div>
                <Button variant="outline" className="mt-3" onClick={addStep}>
                    + Add Step
                </Button>
            </div>
        </div>
    );
}